import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import AppSelect from '../../components/common/AppSelect';

function PerfisFormPage() {
  // Estados do formulário
  const [nome, setNome] = useState('');
  const [tipoAcesso, setTipoAcesso] = useState('interno');

  // Estados de controle
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isProtected, setIsProtected] = useState(false); // Estado para controlar se o perfil é protegido

  const navigate = useNavigate();
  const { id } = useParams();

  const pageTitle = id ? 'Editar Perfil' : 'Novo Perfil';
  const pageSubtitle = id ? `Editando o registro #${id}` : 'Preencha os dados para criar um novo perfil';
  const tipoAcessoOptions = [
    { value: 'interno', label: 'Interno (LDAP SEJUS)' },
    { value: 'externo', label: 'Externo' },
  ];

  // Efeito para buscar o perfil em caso de edição
  useEffect(() => {
    // Regra de negócio: Se o ID na URL for 1, ativa a proteção
    if (id && parseInt(id, 10) === 1) {
      setIsProtected(true);
    }

    if (id) {
      const fetchPerfil = async () => {
        setLoading(true);
        try {
          const response = await api.get(`/perfis/${id}`);
          const { nome, tipo_acesso } = response.data.data;
          setNome(nome);
          setTipoAcesso(tipo_acesso);
        } catch (err) {
          setError('Não foi possível carregar os dados do perfil.');
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchPerfil();
    }
  }, [id]);

  // Manipulador para o envio do formulário
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Regra de negócio: Impede o envio se o perfil for protegido
    if (isProtected) {
      setError("Este perfil não pode ser modificado.");
      return;
    }

    setLoading(true);
    setError(null);

    const perfilData = {
      nome,
      tipo_acesso: tipoAcesso
    };

    try {
      if (id) {
        await api.put(`/perfis/${id}`, perfilData);
      } else {
        await api.post('/perfis', perfilData);
      }
      navigate('/painel/perfis');
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError('Ocorreu um erro ao salvar o perfil. Verifique os dados.');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return (
      <div className="perfis-form-page">
        <LoadingSpinner text="Carregando dados do perfil..." />
      </div>
    );
  }

  return (
    <div className="perfis-form-page">
      <div className="content-header">
        <h1 className="content-title">{pageTitle}</h1>
        <p className="content-subtitle">{pageSubtitle}</p>
      </div>

      <div className="card perfis-form-card">
        <div className="card-body">
          {/* Alerta de perfil protegido */}
          {isProtected && (
            <div className="alert alert-warning">
              <i className="bi bi-shield-lock-fill me-2"></i>
              O perfil <strong>Administrador SEJUS</strong> é protegido e não pode ser modificado.
            </div>
          )}

          <form className="perfis-form" onSubmit={handleSubmit}>
            {error && <div className="alert alert-danger">{error}</div>}

            <fieldset className="perfis-form-section" disabled={isProtected}> {/* Desabilita todos os campos do fieldset */}
              <div className="mb-3">
                <label htmlFor="nome" className="form-label">Nome do Perfil</label>
                <input
                  type="text"
                  className="form-control"
                  id="nome"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  required
                />
              </div>

              <div className="mb-3">
                <label htmlFor="tipoAcesso" className="form-label">Tipo de Acesso</label>
                <AppSelect
                  className="form-app-select"
                  ariaLabel="Tipo de Acesso"
                  options={tipoAcessoOptions}
                  value={tipoAcesso}
                  onChange={setTipoAcesso}
                  placeholder="Selecione..."
                  disabled={isProtected}
                />
              </div>
            </fieldset>

            <div className="perfis-form-actions d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={() => navigate('/painel/perfis')}
                disabled={loading}
              >
                Cancelar
              </button>
              {/* Desabilita o botão de salvar se for protegido ou se estiver carregando */}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || isProtected}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default PerfisFormPage;
